import React, { useEffect, Suspense, lazy, createElement } from 'react';
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
function deferToIdle(fn: () => void) {
  if ('requestIdleCallback' in window) {
    ;
    (window as any).requestIdleCallback(fn, {
      timeout: 3000
    });
  } else {
    setTimeout(fn, 100);
  }
}
export function App() {
  useEffect(() => {
    document.title = 'Megapack Psicología Rentable — Construye Progreso™';
    document.documentElement.lang = 'es';
    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = 'Sistema completo de organización y gestión clínica para psicólogos en Argentina. 6 Tomos, protocolo de admisión, plan maestro anual, maletín de intervenciones y 6 bonos profesionales. Todo tu consultorio ordenado desde el primer día. Acceso inmediato.';
    const url = 'https://psicologia.construyeprogreso.com';
    const image = 'https://cdn.magicpatterns.com/uploads/v7HbDBttB6KwqMN8KX8fXz/hf_20260408_171512_ee149707-ab8e-4b03-a52c-19e854e7886e_(1)-Photoroom.webp';
    // Critical meta tags — run immediately
    setMeta('description', description);
    setMeta('viewport', 'width=device-width, initial-scale=1');
    setMeta('robots', 'index, follow');
    // Defer non-critical meta + structured data to idle
    deferToIdle(() => {
      setMeta('keywords', 'psicología rentable, megapack psicólogos, organización de consultorio, planificación de sesiones, encuadre terapéutico, admisión clínica, honorarios profesionales, Argentina, construye progreso');
      setMeta('author', 'Construye Progreso™');
      if (!document.querySelector('link[rel="canonical"]')) {
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = url;
        document.head.appendChild(canonical);
      }
      setMeta('og:title', 'Megapack Psicología Rentable — Todo tu consultorio resuelto', true);
      setMeta('og:description', description, true);
      setMeta('og:image', image, true);
      setMeta('og:url', url, true);
      setMeta('og:type', 'product', true);
      setMeta('og:locale', 'es_AR', true);
      setMeta('og:site_name', 'Construye Progreso™', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', 'Megapack Psicología Rentable — Construye Progreso™');
      setMeta('twitter:description', description);
      setMeta('twitter:image', image);
    });
    // Defer structured data even further
    deferToIdle(() => {
      if (!document.querySelector('script[data-ld="product"]')) {
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.setAttribute('data-ld', 'product');
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Megapack Psicología Rentable — Sistema Clínico Integral™',
          description,
          image,
          brand: {
            '@type': 'Brand',
            name: 'Construye Progreso™'
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '19990',
            highPrice: '27990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            offerCount: 2
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '612'
          }
        });
        document.head.appendChild(ld);
      }
      if (!document.querySelector('script[data-ld="org"]')) {
        const orgLd = document.createElement('script');
        orgLd.type = 'application/ld+json';
        orgLd.setAttribute('data-ld', 'org');
        orgLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Construye Progreso™',
          url,
          logo: image,
          email: 'construyeprogresoo@gmail.com',
          sameAs: ['https://www.instagram.com/construyeprogresoo']
        });
        document.head.appendChild(orgLd);
      }
      if (!document.querySelector('script[data-ld="faq"]')) {
        const faqLd = document.createElement('script');
        faqLd.type = 'application/ld+json';
        faqLd.setAttribute('data-ld', 'faq');
        faqLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: '¿Qué incluye el Megapack Psicología Rentable?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '6 Tomos completos de gestión clínica, protocolo de admisión, plan maestro anual, maletín de intervenciones, gestión de consultorio y 6 bonos profesionales incluyendo mapa de progreso, rutina semanal y más.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cómo recibo el material?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
            }
          }, {
            '@type': 'Question',
            name: '¿Tiene garantía?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
            }
          }]
        });
        document.head.appendChild(faqLd);
      }
    });
  }, []);
  useEffect(() => {
    // Defer Facebook Pixel aggressively — 8s after idle
    const loadFbPixel = () => {
      if (typeof window !== 'undefined' && !(window as any).fbq) {
        const f = window as any;
        const n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        (window as any).fbq('init', '1215304437119773');
        (window as any).fbq('track', 'PageView');
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        s?.parentNode?.insertBefore(t, s);
      }
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFbPixel, {
        timeout: 8000
      });
    } else {
      setTimeout(loadFbPixel, 8000);
    }
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(245,243,255) 0%, rgb(237,233,254) 30%, rgb(245,243,255) 60%, rgb(248,250,252) 100%)',
      backgroundColor: 'rgb(255, 255, 255)'
    }} data-id="element-2">
        {/* Badge */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-3">
          <span className="text-xs sm:text-sm font-bold text-violet-700 tracking-wide uppercase" data-id="element-4">
            🧠 Sistema Clínico Profesional · Psicólogos en Argentina
          </span>
        </div>

        {/* H1 */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-tight md:leading-[80px] font-black max-w-4xl text-gray-900 m-0 mb-2 text-[28px] leading-[36px]" data-id="element-5">
          <span className="block" data-id="element-6">Dejá de llegar</span>
          <span className="block" data-id="element-7">al consultorio sin saber</span>
          <span className="block text-violet-700" data-id="element-8">qué vas a hacer.</span>
        </h1>

        {/* Mockup — simplified: removed blur glow + 3D perspective for GPU perf */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-9">
          <img src="https://cdn.magicpatterns.com/uploads/1AuUn4dco1P6vxBkxA2G1W/hf_20260420_231501_3cf9079f-b0dd-4c8f-b2b0-8ab6848fec53_(1)-Photoroom.webp" alt="Megapack Psicología Rentable — 6 Tomos + 6 Bonos" width="400" height="400" fetchPriority="high" decoding="async" className="block max-w-full w-full h-auto object-contain relative z-10" style={{
          transform: 'rotateY(-4deg) rotateX(3deg)',
          filter: 'drop-shadow(0 16px 24px rgba(88, 28, 135, 0.25))'
        }} data-id="element-10" />
        </div>

        {/* Identidad profesional */}
        <p className="max-w-xl text-base sm:text-lg font-semibold text-gray-700 leading-7 m-0 mb-4" data-id="element-11">
          El sistema que los psicólogos más organizados de Argentina usan para
          estructurar su admisión, planificar cada sesión con criterio clínico y{' '}
          <span className="text-gray-900 font-black" data-id="element-12">
            cobrar lo que realmente vale su formación.
          </span>
        </p>

        {/* Tagline */}
        <p className="max-w-xl text-lg sm:text-xl font-black text-violet-700 m-0 mb-6" data-id="element-13">
          "6 tomos. Protocolo completo.
          <br className="hidden sm:block" data-id="element-14" />
          Sin improvisar más, sin llegar en blanco más."
        </p>

        {/* Tags */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-15">
          {['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🧠 Criterio Clínico', '✅ Protocolo de Admisión', '📋 Planificación Anual'].map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 border border-violet-500/30 rounded-full px-3 py-1 shadow-sm" data-id="element-16">
              {tag}
            </span>)}
        </div>

        {/* CTA */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Psicología Rentable'
          });
        }
      }} className="block w-full max-w-sm bg-violet-700 text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" data-id="element-17">
          🧠 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* Trust Badges */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-18">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-19">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-20">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-22">
        <Suspense fallback={null} data-id="element-23">
          <SocialProof data-id="element-24" />
          <PainPoints data-id="element-25" />
        </Suspense>

        <div className="cv-auto" data-id="element-26">
          <Suspense fallback={null} data-id="element-27">
            <ProductShowcase data-id="element-28" />
          </Suspense>
        </div>

        <div className="cv-auto" data-id="element-29">
          <Suspense fallback={null} data-id="element-30">
            <WhatYouGet data-id="element-31" />
            <Bonuses data-id="element-32" />
            <Countdown data-id="element-33" />
            <Pricing data-id="element-34" />
          </Suspense>
        </div>

        <div className="cv-auto" data-id="element-35">
          <Suspense fallback={null} data-id="element-36">
            <ProBusiness data-id="element-37" />
            <Guarantee data-id="element-38" />
            <Testimonials data-id="element-39" />
            <FAQ data-id="element-40" />
            <DarkCTA data-id="element-41" />
          </Suspense>
        </div>
      </main>

      <div className="cv-auto" data-id="element-42">
        <Suspense fallback={null} data-id="element-43">
          <Footer data-id="element-44" />
          <PurchaseNotifications data-id="element-45" />
        </Suspense>
      </div>
    </>;
}